import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["container", "toggle", "content"]
    static values = {
        visible: { type: Boolean, default: true },
        position: { type: String, default: "bottom-right" }
    }

    // 过期时间配置（毫秒）
    static expirationTime = 24 * 60 * 60 * 1000 // 24小时

    connect() {
        this.#setupEventListeners()
        this.#loadState()
        // 如果localStorage中没有保存状态，则使用默认的显示状态
        if (localStorage.getItem('floating_chat_visible') === null) {
            this.visibleValue = true
        }
        this.#updateVisibility()
    }

    disconnect() {
        this.#saveState()
    }

    toggle() {
        this.visibleValue = !this.visibleValue
        this.#updateVisibility()
        this.#saveState()
    }

    show() {
        this.visibleValue = true
        this.#updateVisibility()
        this.#saveState()
    }

    hide() {
        this.visibleValue = false
        this.#updateVisibility()
        this.#saveState()
    }

    // 私有方法
    #setupEventListeners() {
        // ESC键关闭悬浮窗
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.visibleValue) {
                this.hide()
            }
        })

        // 监听窗口大小变化，重新调整位置
        window.addEventListener('resize', () => {
            if (this.visibleValue) {
                this.#updateVisibility()
            }
        })
    }

    #updateVisibility() {
        if (this.visibleValue) {
            this.containerTarget.classList.remove('opacity-0', 'scale-95', 'pointer-events-none')
            this.containerTarget.classList.add('opacity-100', 'scale-100')
            this.toggleTarget.classList.add('bg-blue-600', 'text-white')
            this.toggleTarget.classList.remove('bg-gray-800', 'text-gray-300')

            // 在移动端，调整窗口位置
            if (window.innerWidth < 768) {
                // 移动端：更小的窗口，更合适的位置
                this.containerTarget.classList.add('bottom-20', 'right-2', 'w-72')
                this.containerTarget.classList.remove('bottom-16', 'right-0', 'w-80')
            } else {
                // 桌面端：保持原有位置
                this.containerTarget.classList.add('bottom-16', 'right-0', 'w-80')
                this.containerTarget.classList.remove('bottom-20', 'right-2', 'w-72')
            }
        } else {
            this.containerTarget.classList.add('opacity-0', 'scale-95', 'pointer-events-none')
            this.containerTarget.classList.remove('opacity-100', 'scale-100')
            this.toggleTarget.classList.remove('bg-blue-600', 'text-white')
            this.toggleTarget.classList.add('bg-gray-800', 'text-gray-300')
        }
    }

    #loadState() {
        const savedState = localStorage.getItem('floating_chat_visible')
        const savedTimestamp = localStorage.getItem('floating_chat_timestamp')
        const currentTime = Date.now()

        if (savedState !== null && savedTimestamp !== null) {
            // 检查是否过期
            if (currentTime - parseInt(savedTimestamp) < this.constructor.expirationTime) {
                // 未过期，使用保存的状态
                this.visibleValue = JSON.parse(savedState)
            } else {
                // 已过期，清除旧数据并默认显示
                this.#clearExpiredData()
                this.visibleValue = true
                this.#saveState()
            }
        } else {
            // 没有保存的状态，默认显示
            this.visibleValue = true
            this.#saveState()
        }
    }

    #saveState() {
        localStorage.setItem('floating_chat_visible', JSON.stringify(this.visibleValue))
        localStorage.setItem('floating_chat_timestamp', Date.now().toString())
    }

    #clearExpiredData() {
        localStorage.removeItem('floating_chat_visible')
        localStorage.removeItem('floating_chat_timestamp')
    }
}
